import React, { startTransition, useMemo, useState } from "react";

import * as Ariakit from "@ariakit/react";
import styled from "styled-components";

import { Color } from "../../constants/color";
import { Select, SelectItem, SelectProvider } from "../form/select";
import { Check, X } from "lucide-react";
import { matchSorter } from "match-sorter";
import { useTranslation } from "react-i18next";
import {
  BookableEmployeeType,
  RigaPrenotabile,
  SelectedBookableEmployeeType,
} from "../../types";
import Suggerimento from "../tooltip/suggerimento";

const OutlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${Color.WHITE};
  background-color: ${Color.BLUE};
  border: 0;
  padding: 0.3em 1.5em;
  border-radius: 8px;
  font-size: 1.6em;
  font-weight: bold;
  cursor: pointer;
`;

function BookableEmployees({
  employees,
  selectedEmployee,
  corsoMancanteId,
  codiceCorso,
  indice,
  onEmployeeBooked,
  undoEmployeeBooked,
}: // La riga a cui si riferisce la scelta arriva come prop primitive (così il
// memo regge) e torna alle handler, che restano le stesse fra un render e
// l'altro.
RigaPrenotabile & {
  employees: BookableEmployeeType[];
  selectedEmployee?: SelectedBookableEmployeeType | undefined;
  onEmployeeBooked?: (cf: string, riga: RigaPrenotabile) => void;
  undoEmployeeBooked?: (
    selected: SelectedBookableEmployeeType,
    riga: RigaPrenotabile
  ) => void;
}) {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<string>(
    selectedEmployee?.cf || ""
  );
  const [searchEmployeeValue, setSearchEmployeeValue] = useState("");

  const matches = useMemo(() => {
    const employeesArray = Array.from(employees || []);
    return matchSorter(employeesArray, searchEmployeeValue, {
      keys: ["cf", "name"],
      threshold: matchSorter.rankings.CONTAINS,
    });
  }, [employees, searchEmployeeValue]);

  const selectEmployee = Ariakit.useSelectStore({
    defaultValue: selectedValue,
    setValue: (value: string) => {
      setSelectedValue(value);
    },
  });

  function handleEmployeeBooking() {
    // console.log("Employee selected", selectedEmployeeRef.current);
    if (selectedValue && onEmployeeBooked) {
      onEmployeeBooked(selectedValue, { corsoMancanteId, codiceCorso, indice });
    }
  }

  function handleEmployeeUnbooking() {
    if (selectedValue && undoEmployeeBooked) {
      setSelectedValue("");
      selectEmployee.setValue("");
      undoEmployeeBooked(selectedEmployee as SelectedBookableEmployeeType, {
        corsoMancanteId,
        codiceCorso,
        indice,
      });
    }
  }

  const selectStyle = {
    "--padBlock": "0.5rem",
    border: "2px solid",
    borderRadius: "1rem",
    fontSize: "1rem",
    minWidth: "250px",
    borderColor: Color.LIGHT_BLUE,
  } as React.CSSProperties;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexGrow: 1,
        gap: ".8rem",
      }}
    >
      {selectedEmployee ? (
        <>
          <div
            style={{
              minWidth: "250px",
            }}
          >
            {selectedEmployee.name}
          </div>
          <Suggerimento testo={t("attestati-aiuto-rimuovi-partecipante")}>
            <OutlineButton
              type="button"
              aria-label={t("attestati-togli-discente", {
                discente: selectedEmployee.name,
              })}
              onClick={handleEmployeeUnbooking}
              style={{
                backgroundColor: Color.RED,
                padding: ".3rem .5rem",
              }}
            >
              <X color={Color.WHITE} />
            </OutlineButton>
          </Suggerimento>
        </>
      ) : (
        <>
          <Ariakit.ComboboxProvider
            resetValueOnHide
            setValue={(value) => {
              startTransition(() => {
                setSearchEmployeeValue(value);
              });
            }}
          >
            <SelectProvider store={selectEmployee}>
              <Suggerimento testo={t("attestati-aiuto-seleziona-discente")}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.1rem",
                  }}
                >
                  <Select
                    combobox
                    sameWidth={true}
                    style={selectStyle}
                    renderValue={(value) => {
                      if (value === "") return "Seleziona un discente";
                      return employees.find((e) => e.cf === value)?.name || "";
                    }}
                    modal={true}
                  >
                    {matches.map((e, index) => (
                      <SelectItem
                        key={`bookableEmployee_${index}_${e.cf}`}
                        value={e.cf}
                        render={
                          <Ariakit.ComboboxItem
                            render={
                              <p style={{ margin: "0.2rem 0.4rem" }}>{e.name}</p>
                            }
                          />
                        }
                      />
                    ))}
                  </Select>
                </div>
              </Suggerimento>
            </SelectProvider>
          </Ariakit.ComboboxProvider>

          {selectedValue !== "" && (
            <Suggerimento testo={t("attestati-aiuto-aggiungi-partecipante")}>
              <OutlineButton
                type="button"
                aria-label={t("attestati-prenota-discente", {
                  discente:
                    employees.find((e) => e.cf === selectedValue)?.name ?? "",
                })}
                onClick={handleEmployeeBooking}
                style={{
                  backgroundColor: Color.GREEN,
                  padding: ".3rem .5rem",
                }}
              >
                <Check color={Color.WHITE} />
              </OutlineButton>
            </Suggerimento>
          )}
        </>
      )}
    </div>
  );
}

export {
  BookableEmployees,
};
